/* eslint-disable react/jsx-props-no-spreading */
import { useCallback, useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { Form, message, Card } from 'antd'

import { Formik, useFormikContext } from 'formik'
import humps from 'humps'
import { useNavigate, useParams } from 'react-router-dom'
import startCase from 'lodash/startCase'
import debounce from 'lodash/debounce'
import get from 'lodash/get'
import { createUseStyles } from 'react-jss'
import { formElementPropsGenerator } from '../../lib/forms'
import { SubmitButton } from '../Forms'
import api from '../../api'
import Checkbox from '../Forms/Checkbox'
import RadioButton from '../Forms/RadioButton'
import QuestionForm from '../Reports/QuestionForm'
import {
  formatInitialValues,
  formatSavedValues,
  getLabels,
  getPlaceholders,
  buildValidationSchema,
  formatBatteryValue,
} from '../Reports/form-utils'
import FormItem from '../Reports/FormItem'
import FormItems from '../Common/FormItems'

function filterCycle30Fields(formSections) {
  return formSections.filter(section => section.restrictedTo !== 'cycle_30')
}

const useStyles = createUseStyles({
  sectionCard: {
    marginTop: '16px',
    marginBottom: '16px',
  },
})

// eslint-disable-next-line react/prop-types
function FormatBatteryValue({ fieldId }) {
  const { values, setFieldValue } = useFormikContext()
  const [debouncedBattery, setDebouncedBattery] = useState(
    formatBatteryValue(values[fieldId] || ''),
  )

  // Keep a debounced battery value.
  const debounceBattery = debounce(
    nextValue => setDebouncedBattery(formatBatteryValue(nextValue)),
    2000,
  )

  useEffect(() => {
    // Start debouncing if the battery input changes
    debounceBattery(values[fieldId])
    return () => {
      // Stop watching on component destruction
      debounceBattery.cancel()
    }
  }, [
    values,
    setFieldValue,
    setDebouncedBattery,
    debouncedBattery,
    debounceBattery,
    fieldId,
  ])

  // Keep battery field updated with debounced value
  useEffect(() => {
    setFieldValue(fieldId, debouncedBattery)
  }, [debouncedBattery, fieldId, setFieldValue])

  return null
}

export default function ReportForm({ report, practiceId, noValidation = false }) {
  const navigate = useNavigate()
  const { id } = useParams()
  const classes = useStyles()
  const [answeredQuestion, setAnsweredQuestion] = useState(false)
  const [filterFields, setFilterFields] = useState(report.filteredFields)

  const {
    formSections: initialFormSections,
    fieldValues,
    activity,
    modifierMinus26,
    critical,
    device,
    additionalQuestion,
    hasFile,
    hasLongCycleAndDeviceWithFullLength,
    suggestions,
    nonBillable,
  } = report

  const initialActivity = nonBillable ? 'non_billable' : activity

  const showQuestionForm = !hasFile && !answeredQuestion && additionalQuestion

  const filteredFormSections = initialFormSections.filter(
    section =>
      section.name !== 'Office Check Coding' &&
      section.name !== 'Office Check Modifier',
  )

  const formSections = filterFields
    ? filterCycle30Fields(filteredFormSections)
    : filteredFormSections

  // FIXME: fields should come from api
  const batterySection = formSections.find(
    section => section.name === 'battery',
  )
  let batteryField = batterySection?.fields?.find(
    field => field.itemName === 'estimated_battery_longevity',
  )

  if (batteryField) {
    batteryField = {
      ...batteryField,
      dataType: 'text',
      description: `Express the longevity in years. If it's under a year, express it in months.`,
    }
    delete batteryField.options
  }

  const handleEdit = useCallback(
    async ({
      activity: activityField,
      modifierMinus26: modifierField,
      critical: criticalField,
      ...values
    }) => {
      const reportValues = formatSavedValues(values, formSections)
      const isNonBillable = activityField === 'non_billable'

      try {
        if (filterFields) {
          await api.reports.turnOff(id)
        }
        await api.officeChecks.saveReportForm(id, practiceId, {
          reportValues,
          activity: isNonBillable ? null : activityField,
          modifier_minus_26: modifierField,
          critical: criticalField,
          non_billable: isNonBillable,
        })

        navigate(`/practices/${practiceId}/office-checks/${id}`)
        message.success('BioBridge report has been edited')
      } catch (e) {
        message.error(
          `There was an error editing a BioBridge report: ${e.message}`,
        )
      }
    },
    [filterFields, formSections, id, navigate, practiceId],
  )
  const handleQuestion = useCallback(
    async ({ question }) => {
      try {
        if (question === 'yes') {
          setAnsweredQuestion(true)
          return
        }

        if (question === 'no' && hasLongCycleAndDeviceWithFullLength) {
          setFilterFields(true)
          setAnsweredQuestion(true)
          return
        }

        await api.reports.turnOff(id, true)
        message.success('BioBridge report has been rejected')
        navigate('/unreviewed-reports')
      } catch (e) {
        message.error(
          `There was an error rejected a BioBridge report: ${e.message}`,
        )
      }
    },
    [hasLongCycleAndDeviceWithFullLength, id, navigate],
  )

  if (showQuestionForm) {
    return (
      <QuestionForm
        additionalQuestion={additionalQuestion}
        handleQuestion={handleQuestion}
      />
    )
  }

  const validationSchema = buildValidationSchema(formSections)
  const formType = humps.camelize(
    get(device, 'deviceModel.deviceType.formType'),
  )
  const propsFor = formElementPropsGenerator({
    formName: 'report-edit-form',
    labels: {
      ...getLabels(formSections),
      modifierMinus26:
        'Apply Modifier -26 (technical component done by an industry rep)',
      critical: 'Requires attention?',
    },
    placeholders: getPlaceholders(formSections),
  })

  return (
    <Formik
      onSubmit={handleEdit}
      initialValues={{
        ...formatInitialValues(fieldValues, formSections, formType, report),
        activity: initialActivity,
        modifierMinus26,
        critical,
      }}
      validationSchema={noValidation ? null : validationSchema}
    >
      {({ handleSubmit, isSubmitting, isValid, submitCount }) => (
        <Form onFinish={handleSubmit} noValidation>
          {batteryField && <FormatBatteryValue fieldId={batteryField.itemId} />}
          {formSections.map(section => (
            <Card
              type="inner"
              title={startCase(section.name)}
              key={section.name}
              className={classes.sectionCard}
              size="small"
            >
              <FormItems>
                {section.fields.map(field => (
                  <FormItem
                    propsFor={propsFor}
                    key={field.itemId}
                    field={field}
                    formType={formType}
                    layout="vertical"
                    suggestions={
                      suggestions.find(
                        suggestion => suggestion.reportItemId === field.itemId,
                      )?.suggestions || []
                    }
                  />
                ))}
              </FormItems>
            </Card>
          ))}
          <Card
            type="inner"
            title="Office Check Modifier"
            className={classes.sectionCard}
            size="small"
          >
            <Checkbox {...propsFor('modifierMinus26')} />
          </Card>
          <Card
            type="inner"
            title="Requires Attention"
            className={classes.sectionCard}
            size="small"
          >
            <Checkbox {...propsFor('critical')} />
          </Card>
          <Card type="inner" title="Activity" className={classes.sectionCard} size="small">
            <RadioButton
              {...propsFor('activity')}
              options={[
                { value: 'interrogation', label: 'Was this an interrogation?' },
                { value: 'programming', label: 'Was this a programming?' },
                { value: 'non_billable', label: 'Non Billable' },
              ]}
            />
          </Card>
          <SubmitButton
            isSubmitting={isSubmitting}
            isValid={isValid}
            submitCount={submitCount}
            title="Save Report"
          />
        </Form>
      )}
    </Formik>
  )
}

ReportForm.propTypes = {
  report: PropTypes.shape().isRequired,
  practiceId: PropTypes.string.isRequired,
  noValidation: PropTypes.bool,
}
